import { useCallback, useMemo, useRef, useState } from 'react';

import type {
  CoachNotificationPreferenceKey,
  CoachNotificationPreferencesState,
} from '@/features/coach/components/notification-preferences';
import {
  useCoachNotificationPreferencesQuery,
  useUpdateCoachNotificationPreferencesMutation,
} from '@/features/coach/queries/notification-preferences';

/** Toggles the coach has flipped that haven't been confirmed by the server yet. */
type PendingToggles = Partial<CoachNotificationPreferencesState>;

/**
 * View-model for the Coach Notification Preferences screen.
 *
 * The switches must respond on the same frame as the tap, so this hook — not the query cache —
 * owns the optimistic state: a tap writes the new value into `pending` synchronously and the row
 * renders `server ⊕ pending`. The PATCH then runs in the background and the pending entry is
 * released once it settles, handing the row back to server truth (which also rolls the switch back
 * on failure).
 *
 * Releasing is guarded per key: rapid taps on the same switch start overlapping requests, and only
 * the newest one may clear that key — otherwise an earlier response would drop the overlay and
 * briefly show the value the coach just moved away from.
 */
export function useCoachNotificationPreferences() {
  const { data, isPending } = useCoachNotificationPreferencesQuery();
  const { mutate } = useUpdateCoachNotificationPreferencesMutation();

  const [pending, setPending] = useState<PendingToggles>({});
  const toggleSeqRef = useRef<Partial<Record<CoachNotificationPreferenceKey, number>>>({});

  const handleToggle = useCallback(
    (key: CoachNotificationPreferenceKey, value: boolean) => {
      // 1. Instant, synchronous UI feedback — no await, no cache round-trip.
      setPending(current => ({ ...current, [key]: value }));

      const seq = (toggleSeqRef.current[key] ?? 0) + 1;
      toggleSeqRef.current[key] = seq;

      // 2. Persist in the background; release the overlay only if this is still the latest intent
      //    for this key, so the row falls back to whatever the server confirmed (or reverts on error).
      mutate(
        { [key]: value },
        {
          onSettled: () => {
            if (toggleSeqRef.current[key] !== seq) return;
            setPending(current => {
              if (!(key in current)) return current;
              const { [key]: _released, ...rest } = current;
              return rest;
            });
          },
        },
      );
    },
    [mutate],
  );

  const preferences = useMemo<CoachNotificationPreferencesState | undefined>(
    () => (data ? { ...data, ...pending } : undefined),
    [data, pending],
  );

  // `undefined` until the API answers: the screen renders skeleton rows rather than guessing a
  // value the coach never saved.
  return { preferences, handleToggle, isLoading: isPending };
}
